import { computed, ref } from "vue";
import {
  DoughnutChartBgColors,
  calculateTotalMonthlySubscriptions,
  conversionRate,
  getLast12Months,
} from "./helper";
import { facilityData } from "./data";

const incidentInfo = ref({});
const dashboardData = ref([]);
const dashboardLoading = ref(false);
const monthlyNewSubscriptionsLoading = ref(false);
const monthlyNewSubscriptions = ref([]);

const organizationDescendantsLoading = ref(false);
const regions = ref([]);
const facilities = ref([]);
const selectedRegionsId = ref([]);
const selectedFacilityIds = ref([]);

const last12Months = getLast12Months();

const filteredFacilities = computed(() => facilityData);

export function computeTotal(key) {
  return computed(() =>
    dashboardData.value?.reduce((acc, current) => (acc += current[key] || 0), 0)
  );
}

export default function useDashboard() {
  const totalCurrentActiveSubscriptions = computeTotal(
    "currentActiveSubscriptionMTD"
  );
  const totalNumberOfMoveIns = computeTotal("totalMoveInMTD");
  const avgSubscriptionDuration = computed(
    () =>
      computeTotal("avgSubscriptionDuration").value /
      selectedFacilityIds.value.length
  );
  const subscriptionPrice = computed(
    () =>
      computeTotal("subscriptionPrice").value / selectedFacilityIds.value.length
  );

  const totalUnits = computeTotal("totalUnit");
  const activeSubscriptions = computeTotal("activeSubscription");

  const totalLifeTimeRevenueThisMonth = computed(() => {
    if (
      avgSubscriptionDuration.value === 0 ||
      subscriptionPrice.value === 0 ||
      totalCurrentActiveSubscriptions.value === 0
    )
      return 0;
    const revenue =
      avgSubscriptionDuration.value *
      subscriptionPrice.value *
      totalCurrentActiveSubscriptions.value;
    return Math.round(revenue);
  });

  const totalMissedLifeTimeRevenueThisMonth = computeTotal(
    "missedLifetimeRevenue"
  );
  const totalPenetrationRate = computed(() => {
    if (activeSubscriptions.value === 0 || totalUnits.value === 0) return 0;
    const penetrationRate =
      (activeSubscriptions.value / totalUnits.value) * 100;

    return Math.round(penetrationRate);
  });

  const totalAnnualizedLifetimeRevenue = computeTotal("annualizedRevenue");
  const totalConversionsRateOfThisMonth = computed(() =>
    Math.round(
      conversionRate(totalCurrentActiveSubscriptions, totalNumberOfMoveIns)
    )
  );
  const totalConversion = computed(
    () =>
      `${totalCurrentActiveSubscriptions.value}/${totalNumberOfMoveIns.value}`
  );

  //  *********************************Dummy*************************************
  //  *********************************Dummy*************************************
  const conversionTable = computed(() => ({
    headers: [
      { text: "Facility Name", value: "facilityName" },
      {
        text: "Current Active Subscriptions (MTD)",
        value: "newSubscriptionsMTD",
      },
      { text: "Total Move-Ins (MTD)", value: "totalMoveInMTD" },
      { text: "Conversion %", value: "conversion" },
      { text: "Last Month Conversion", value: "lastMonthConversion" },
      {
        text: "Previous Month Conversion (Last Year)",
        value: "previousMonthConversion",
      },
      { text: "3-Months Rolling Average", value: "threeMonthsRollingAverage" },
    ],
    body: dashboardData.value.map((facilityData) => ({
      facilityName: facilityData.name,
      newSubscriptionsMTD: facilityData.currentActiveSubscriptionMTD,
      totalMoveInMTD: facilityData.totalMoveInMTD,
      conversion: facilityData.conversion,
      lastMonthConversion: facilityData.lastMonthConversion,
      previousMonthConversion: facilityData.previousMonthConversion,
      threeMonthsRollingAverage: facilityData.threeMonthsRollingAverage,
    })),
  }));

  const lifetimeRevenueAddsTable = computed(() => ({
    headers: [
      { text: "Facility Name", value: "facilityName" },
      { text: "Added Subscriptions (MTD)", value: "subscriptions" },
      { text: "Missed Subscriptions (MTD)", value: "missedSubscriptions" },
      { text: "Total Move-Ins (MTD)", value: "totalMoveInMTD" },
      {
        text: "Average Subscription Duration (Months)",
        value: "avgSubscriptionDuration",
      },
      { text: "Lifetime Adds ($)", value: "lifetimeRevenueAdds" },
      { text: "Missed Potential Revenue ($)", value: "missedPotentialRevenue" },
    ],
    body: dashboardData.value.map((facilityData) => ({
      facilityName: facilityData.name,
      subscriptions: facilityData.currentActiveSubscriptionMTD,
      missedSubscriptions: facilityData.missedSubscriptions,
      totalMoveInMTD: facilityData.totalMoveInMTD,
      avgSubscriptionDuration: facilityData.avgSubscriptionDuration,
      lifetimeRevenueAdds: facilityData.lifetimeRevenueAdds,
      missedPotentialRevenue: facilityData.missedLifetimeRevenue,
    })),
  }));

  const missedPotentialRevenue = computed(() => ({
    headers: [
      { text: "Facility Name", value: "facilityName" },
      { text: "Missed Subscriptions (MTD)", value: "missedSubscriptions" },
      { text: "Total Move-Ins (MTD)", value: "totalMoveInMTD" },
      { text: "Avg Subscription (Month)", value: "subscriptionMTD" },
      { text: "Missed Potential Revenue ($)", value: "missedLifetimeRevenue" },
    ],
    body: dashboardData.value.map((facilityData) => ({
      facilityName: facilityData.name,
      missedSubscriptions: facilityData.missedSubscriptions,
      totalMoveInMTD: facilityData.totalMoveInMTD,
      subscriptionMTD: facilityData.subscriptionMTD,
      missedLifetimeRevenue: facilityData.missedLifetimeRevenue,
    })),
  }));

  const penetrationTable = computed(() => ({
    headers: [
      { text: "Facility Name", value: "facilityName" },
      { text: "Current Subscriptions", value: "activeSubscription" },
      { text: "Total Units", value: "totalUnit" },
      { text: "Facility Penetrations (%)", value: "facilityPenetrations" },
      { text: "Annualized Lifetime ($)", value: "annualizedRevenue" },
    ],
    body: dashboardData.value.map((facilityData) => ({
      facilityName: facilityData.name,
      activeSubscription: facilityData.activeSubscription,
      totalUnit: facilityData.totalUnit,
      facilityPenetrations: Math.round(facilityData.facilityPenetrations),
      annualizedRevenue: facilityData.annualizedRevenue,
    })),
  }));
  //  *********************************Dummy*************************************
  //  *********************************Dummy*************************************

  const totalMonthlyNewSubscriptionsChartData = computed(() => {
    const { months } = calculateTotalMonthlySubscriptions(
      monthlyNewSubscriptions.value
    );
    const ids =
      monthlyNewSubscriptions.value?.map(({ facilityId }) => facilityId) ?? [];
    return {
      labels: months,
      datasets: [
        {
          label: "Monthly New Subscriptions",
          backgroundColor: "rgba(77, 85, 202, 0.8)",
          data: [323, 333, 339, 346, 345, 365, 366, 370, 385, 400, 410, 69],
          ids,
        },
      ],
    };
  });

  const MTDNewSubscribersChartData = computed(() => {
    const monthlySubscriptions = monthlyNewSubscriptions.value ?? [];
    // const { labels, datasetData } = categorizeData(
    //   monthlySubscriptions,
    //   7,
    //   "subscriptionsCount",
    //   "facilityName"
    // );

    return {
      labels: [
        "3x3 Self Storage - Denton",
        "3x3 Self Storage - Fort Worth",
        "3x3 Self Storage - Waco",
        "3x3 Self Storage - lewisville",
        "3x3 Self Storage - Abllene",
        "Others",
      ],
      datasets: [
        {
          label: "MTD New Subscribers",
          backgroundColor: DoughnutChartBgColors,
          data: [57, 59, 73, 76, 54, 45, 32],
        },
      ],
    };
  });

  const MTDNewSubscribersTableData = computed(() => {
    const dessertsMap = {};

    // Aggregate subscriptions count for each facility name
    monthlyNewSubscriptions.value?.forEach((item) => {
      const { facilityName, subscriptionsCount } = item;
      dessertsMap[facilityName] =
        (dessertsMap[facilityName] || 0) + subscriptionsCount;
    });

    // Transform aggregated data into array of objects
    const data = Object.keys(dessertsMap).map((name, index) => ({
      rowNumber: index + 1,
      name,
      subscriptions: dessertsMap[name],
    }));

    // Calculate total subscriptions
    const totalSubscriptions = data.reduce(
      (total, { subscriptions }) => total + subscriptions,
      0
    );
    return {
      headers: [
        { text: "", align: "start", sortable: false, value: "rowNumber" },
        { text: "Facility", align: "start", sortable: false, value: "name" },
        { text: "Subscriptions", value: "subscriptions" },
      ],

      data: [
        {
          name: "3x3 Self Storage - Denton",
          subscriptions: 57,
          id: "909a1553-74ac-40dc-b33b-490b44218161b",
        },
        {
          name: "3x3 Self Storage - Fort Worth",
          subscriptions: 59,
          id: "909a1553-74ac-40dc-b33b-490b44218161a",
        },
        {
          name: "3x3 Self Storage - Waco",
          subscriptions: 73,
          id: "70640695-811d-48bd-9c39-6dc78ff9014d",
        },
        {
          name: "3x3 Self Storage - lewisville",
          subscriptions: 76,
          id: "909a1553-74ac-40dc-b33b-490b44218161",
        },
        {
          name: "3x3 Self Storage - Abllene",
          subscriptions: 54,
          id: "909a1553-74ac-40dc-b33b-490b44218161q",
        },
        {
          name: "Frisco Self Storage",
          subscriptions: 45,
          id: "268900b0-08b4-4acd-aae6-fe8097cb544d",
        },
        {
          name: "Ideal Self Storage",
          subscriptions: 32,
          id: "909a1553-74ac-40dc-b33b-490b44218161s",
        },
      ],
      totalSubscriptions: 396,
    };
  });

  const allActiveSubscriptionsChartData = computed(() => {
    const dashboardDataValue = dashboardData.value ?? [];

    return {
      labels: [
        "3x3 Self Storage - Denton",
        "3x3 Self Storage - Fort Worth",
        "3x3 Self Storage - Waco",
        "3x3 Self Storage - lewisville",
        "3x3 Self Storage - Abllene",
        "Others",
      ],
      datasets: [
        {
          label: "Active Subscriptions",
          backgroundColor: DoughnutChartBgColors,
          data: [153, 103, 122, 97, 81, 79],
        },
      ],
    };
  });

  const allActiveSubscriptionsTableData = computed(() => {
    const data =
      dashboardData.value?.map((item, index) => ({
        rowNumber: index + 1,
        name: item.name,
        subscriptions: item.totalActiveSubscriptions,
      })) ?? [];

    const totalSubscriptions = data.reduce(
      (total, { subscriptions }) => (total += subscriptions),
      0
    );
    return {
      headers: [
        {
          text: "",
          align: "start",
          sortable: false,
          value: "rowNumber",
        },
        {
          text: "Facility",
          align: "start",
          sortable: false,
          value: "name",
        },
        { text: "Subscriptions", value: "subscriptions" },
      ],
      data: [
        {
          name: "3x3 Self Storage - Denton",
          subscriptions: 153,
          id: "909a1553-74ac-40dc-b33b-490b44218161b",
        },
        {
          name: "3x3 Self Storage - Fort Worth",
          subscriptions: 103,
          id: "909a1553-74ac-40dc-b33b-490b44218161q",
        },
        {
          name: "3x3 Self Storage - Waco",
          subscriptions: 122,
          id: "909a1553-74ac-40dc-b33b-490b44218161a",
        },
        {
          name: "3x3 Self Storage - lewisville",
          subscriptions: 97,
          id: "909a1553-74ac-40dc-b33b-490b44218161",
        },
        {
          name: "3x3 Self Storage - Abllene",
          subscriptions: 81,
          id: "70640695-811d-48bd-9c39-6dc78ff9014d",
        },
        {
          name: "Frisco Self Storage",
          subscriptions: 79,
          id: "268900b0-08b4-4acd-aae6-fe8097cb544d",
        },
        {
          name: "Ideal Self Storage",
          subscriptions: 7,
          id: "909a1553-74ac-40dc-b33b-490b44218161s",
        },
      ],
      totalSubscriptions: 642,
    };
  });

  const totalRevenueThisYear = {
    labels: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
    ],
    datasets: [
      {
        label: "Total Revenue ($)",
        backgroundColor: "rgba(255, 196, 196, 0.8)",
        data: [3828, 3876, 3900, 3984, 4044, 4100, 4176, 4356, 1956],
        borderColor: "#d50d41",
        tension: 0.1,
      },
    ],
  };

  const averageDurationPerVisitsChart = {
    labels: [">20 mins", "10 - 20 mins", "2 - 10 mins", "<2 mins"],
    datasets: [
      {
        label: "Average Duration Per Visit (MTD)",
        data: [1, 1, 1, 1],
        realData: [1, 0, 0, 4],
        backgroundColor: [
          "rgb(119,93,208)",
          "rgb(254,176,25)",
          "rgb(0,227,150)",
          "rgb(255,69,96)",
        ],
      },
    ],
  };

  const averageVisitChart = {
    labels: [">40", "20 - 40", "1 - 20", "<1"],
    datasets: [
      {
        label: "Average no.of Visit (MTD)",
        data: [1, 1, 1, 1],
        realData: [1, 0, 0, 4],
        backgroundColor: [
          "rgb(119,93,208)",
          "rgb(254,176,25)",
          "rgb(0,227,150)",
          "rgb(255,69,96)",
        ],
      },
    ],
  };

  const MTDSubscribersPieChart = {
    labels: ["Today", "Yesterday"],
    datasets: [
      {
        label: "MTD New Subscribers",
        data: [1, 1],
        realData: [27, 36],
        backgroundColor: ["rgb(119,93,208)", "rgb(255,69,96)"],
      },
    ],
  };

  const MTDSubscribersPieChartTableData = {
    headers: [
      { text: "", align: "start", sortable: false, value: "rowNumber" },
      { text: "Facility", align: "start", sortable: false, value: "name" },
      { text: "Today", value: "today" },
      { text: "Yesterday", value: "yesterday" },
    ],

    data: [
      {
        name: "3x3 Self Storage - Denton",
        today: 9,
        yesterday: 10,
        id: "909a1553-74ac-40dc-b33b-490b44218161a",
      },
      {
        name: "3x3 Self Storage - Fort Worth",
        today: 6,
        yesterday: 8,
        id: "909a1553-74ac-40dc-b33b-490b44218161b",
      },
      {
        name: "3x3 Self Storage - Waco",
        today: 5,
        yesterday: 7,
        id: "70640695-811d-48bd-9c39-6dc78ff9014d",
      },
      {
        name: "3x3 Self Storage - lewisville",
        today: 4,
        yesterday: 6,
        id: "909a1553-74ac-40dc-b33b-490b44218161",
      },
      {
        name: "3x3 Self Storage - Abllene",
        today: 3,
        yesterday: 5,
        id: "909a1553-74ac-40dc-b33b-490b44218161q",
      },
    ],
    todayTotalSubscriptions: 27,
    yesterdayTotalSubscriptions: 36,
  };

  const averageConversionPercentage = {
    labels: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
    ],
    datasets: [
      {
        label: "Conversion (%)",
        backgroundColor: "rgba(196, 200, 255, 0.8)",
        data: [79, 80, 84, 87, 89, 96, 106, 107, 70],
        borderColor: "#323487",
        tension: 0.6,
      },
    ],
  };

  return {
    dashboardData,
    dashboardLoading,
    totalConversionsRateOfThisMonth,
    totalConversion,
    totalLifeTimeRevenueThisMonth,
    totalMissedLifeTimeRevenueThisMonth,
    totalPenetrationRate,
    totalAnnualizedLifetimeRevenue,
    regions,
    facilities,
    selectedRegionsId,
    selectedFacilityIds,
    organizationDescendantsLoading,
    filteredFacilities,
    allActiveSubscriptionsChartData,
    allActiveSubscriptionsTableData,
    totalRevenueThisYear,
    averageConversionPercentage,
    monthlyNewSubscriptions,
    monthlyNewSubscriptionsLoading,
    totalMonthlyNewSubscriptionsChartData,
    MTDNewSubscribersChartData,
    MTDNewSubscribersTableData,
    conversionTable,
    lifetimeRevenueAddsTable,
    missedPotentialRevenue,
    penetrationTable,
    averageDurationPerVisitsChart,
    averageVisitChart,
    MTDSubscribersPieChart,
    MTDSubscribersPieChartTableData,
    incidentInfo,
  };
}
